/**
 * @name AuthService
 * @description Servicio para la autenticacion de usuario (Login y Logout)
 * @author Ricardo Guerra
 */

import axios from 'axios';
import moment from 'moment';
import { Environment } from '@config';
import { AuthStorageService } from './auth-storage.service';
import { TwoFactorService } from './two-factor.service';
// import CryptoJS from 'crypto-js';

const getAuthorizationData = () => {
  try {
    return JSON.parse(localStorage.getItem('ls.authorizationData')) || false;
  } catch (error) {
    return false;
  }
};

/**
 * Consulta si el usuario esta autenticado
 * @return {boolean} isAuth
 */
const isAuth = () => {
  const authorizationData = getAuthorizationData();
  return (authorizationData && authorizationData.expirationDate && (new Date() < new Date(authorizationData.expirationDate)));
};

const hasActiveSession = () => {
  return Boolean(
    localStorage.getItem('ls.infoUsuario') &&
    localStorage.getItem('ls.authorizationData') &&
    isAuth()
  );
};

// Informacion de autenticacion
const authentication = {
  get isAuth() {
    return isAuth();
  },
  get hasActiveSession() {
    return hasActiveSession();
  }
};

// URL Authentication Endpoint
const LEGACY_AUTH_URL = Environment.getCurrent().endpoints.auth;
const INTERACTIVE_AUTH_PATH = '/Auth/Login';

const getCurrentLanguage = () => {
  try {
    const storedLanguage = JSON.parse(localStorage.getItem('ls.ty-Language'))?.language;
    if (storedLanguage) {
      return storedLanguage;
    }
  } catch (error) {
    // Fall back a idioma del navegador.
  }

  const browserLanguage = navigator.language || navigator.userLanguage || 'es';
  return browserLanguage.split('-')[0];
};

const getLegacyHeaders = () => {
  return {
    'Content-Type': 'application/x-www-form-urlencoded',
    'Accept-Language': getCurrentLanguage()
  };
};

const getInteractiveHeaders = () => {
  return {
    'Content-Type': 'application/json',
    'Accept-Language': getCurrentLanguage()
  };
};

const isAuthenticatedResponse = (data = {}) => {
  return Boolean(data.access_token && data.expires_in);
};

const normalizeAuthenticatedResponse = (data) => {
  return {
    type: 'authenticated',
    authData: data,
    raw: data
  };
};

const normalizeChallengeResponse = (data) => {
  return {
    type: 'challenge',
    challengeData: TwoFactorService.normalizeChallengeResponse(data),
    raw: data
  };
};

const createResolvedRequestError = (data, fallbackMessage = 'UNEXPECTED_AUTH_RESPONSE') => {
  const error = new Error(
    data?.message ||
    data?.Message ||
    data?.error_description ||
    data?.ErrorDescription ||
    fallbackMessage
  );

  error.response = {
    data,
    status: 200
  };

  return error;
};

const REMEMBER_DEVICE_REJECTION_CODES = [
  'REMEMBER_DEVICE_REJECTED',
  'INVALID_REMEMBER_DEVICE_TOKEN',
  'REMEMBER_DEVICE_TOKEN_INVALID',
  'REMEMBER_DEVICE_TOKEN_EXPIRED',
  'REMEMBER_DEVICE_TOKEN_MISMATCH'
];

const getAuthErrorText = (responseData) => {
  if (!responseData) {
    return '';
  }

  if (typeof responseData === 'string') {
    return responseData;
  }

  return [
    responseData.message,
    responseData.error_description,
    responseData.error,
    responseData.code,
    responseData.errorCode
  ].filter(Boolean).join(' ');
};

const isExplicitRememberDeviceRejection = (error) => {
  const responseData = error?.response?.data;

  if (!responseData) {
    return false;
  }

  if (
    responseData.RememberDeviceRejected === true ||
    responseData.rememberDeviceRejected === true
  ) {
    return true;
  }

  const responseCode = String(
    responseData.code ||
    responseData.errorCode ||
    responseData.ErrorCode ||
    ''
  ).toUpperCase();

  if (REMEMBER_DEVICE_REJECTION_CODES.includes(responseCode)) {
    return true;
  }

  const errorText = getAuthErrorText(responseData);

  return /(remember\s*device|rememberdevicetoken|remember_device|trusted\s*device)/i.test(errorText);
};

const persistAuthenticationData = (data, loginData = {}) => {
  const useRefreshTokens = loginData.useRefreshTokens !== false;
  const userName = data.userName || loginData.userName;

  if (useRefreshTokens) {
    localStorage.setItem('ls.authorizationData', JSON.stringify({
      token: data.access_token,
      refreshToken: data.refresh_token,
      userName,
      useRefreshTokens: true,
      expirationDate: moment().add(data.expires_in, 'seconds')
    }));
  } else {
    localStorage.setItem('ls.authorizationData', JSON.stringify({
      token: data.access_token,
      refreshToken: '',
      userName,
      useRefreshTokens: false,
      expirationDate: moment().add(data.expires_in, 'seconds')
    }));
  }

  if (loginData.keepSession && data.pwd) {
    localStorage.setItem('ls.credentials', JSON.stringify({
      userName: loginData.userName,
      password: data.pwd,
    }));
  }

  if (data.pwd) {
    localStorage.setItem('creds', JSON.stringify({
      userName: loginData.userName,
      password: data.pwd,
    }));
  }

  if (data.rememberDeviceToken) {
    AuthStorageService.saveRememberDeviceData({
      rememberDeviceToken: data.rememberDeviceToken,
      rememberDeviceExpiresInSeconds: data.rememberDeviceExpiresInSeconds
    });
  }

  return data;
};

const getLoginUrl = () => {
  return `${window.location.origin}/talentry/#/login`;
};

/**
 * Iniciar sesion
 * @param {object} data { user, password }
 * @return {any} promise
 */
const login = (loginData) => {
  const clientId = Environment.getCurrent().clientId;

  // eslint-disable-next-line no-undef
  // const secretKey = CryptoJS.enc.Utf8.parse(process.env.SECRET_KEY);  // La clave debe ser la misma que en C#
  // const iv = CryptoJS.enc.Hex.parse('1234567890abcdef');  // IV igual que en C#

  // // Encriptar username y password usando CryptoJS
  // const encryptedUserName = CryptoJS.DES.encrypt(loginData.userName, secretKey, {
  //   iv,
  //   mode: CryptoJS.mode.CBC,
  //   padding: CryptoJS.pad.Pkcs7
  // }).toString();

  // const encryptedPassword = CryptoJS.DES.encrypt(loginData.password, secretKey, {
  //   iv,
  //   mode: CryptoJS.mode.CBC,
  //   padding: CryptoJS.pad.Pkcs7
  // }).toString();
  const encryptedUserName = loginData.userName;
  const encryptedPassword = loginData.password;

  const authData = `grant_type=password&username=${encodeURIComponent(encryptedUserName)}&password=${encodeURIComponent(encryptedPassword)}&client_id=${clientId}`;

  // NOTA: Desde el Login se envia siempre en TRUE useRefreshTokens
  return axios.post(LEGACY_AUTH_URL, authData, { headers: getLegacyHeaders() })
    .then(({ data }) => {
      return persistAuthenticationData(data, loginData);
    });
};

const interactiveLogin = (loginData, options = {}) => {
  const { retryOnRememberDeviceRejection = true } = options;
  const currentEnvironment = Environment.getCurrent();
  const payload = AuthStorageService.buildInteractiveLoginPayload({
    userName: loginData.userName,
    password: loginData.password,
    clientId: currentEnvironment.clientId,
    keepSession: loginData.keepSession,
    rememberDevice: loginData.rememberDevice
  });

  return axios.post(`${currentEnvironment.endpoints.admin}${INTERACTIVE_AUTH_PATH}`, payload, {
    headers: getInteractiveHeaders()
  }).then(({ data }) => {
    if (TwoFactorService.isChallengeResponse(data)) {
      return normalizeChallengeResponse(data);
    }

    if (isAuthenticatedResponse(data)) {
      persistAuthenticationData(data, loginData);
      return normalizeAuthenticatedResponse(data);
    }

    return data;
  }).catch((error) => {
    const hasRememberDeviceToken = Boolean(AuthStorageService.getRememberDeviceToken());

    if (
      retryOnRememberDeviceRejection &&
      hasRememberDeviceToken &&
      isExplicitRememberDeviceRejection(error)
    ) {
      AuthStorageService.clearRememberDeviceToken();
      return interactiveLogin(loginData, { retryOnRememberDeviceRejection: false });
    }

    throw error;
  });
};

const verifyTwoFactorChallenge = ({ challengeId, code, rememberDevice = false }, loginData = {}) => {
  const payload = AuthStorageService.buildTwoFactorVerifyPayload({
    challengeId,
    code,
    rememberDevice
  });

  return TwoFactorService.verify(payload)
    .then((data) => {
      if (data?.Success === false || data?.success === false) {
        throw createResolvedRequestError(data, 'TWO_FACTOR_VERIFY_FAILED');
      }

      if (!isAuthenticatedResponse(data)) {
        throw createResolvedRequestError(data, 'UNEXPECTED_VERIFY_RESPONSE');
      }

      persistAuthenticationData(data, loginData);
      return normalizeAuthenticatedResponse(data);
    });
};

const resendTwoFactorChallenge = (challengeId) => {
  return TwoFactorService.resend(challengeId)
    .then((data) => {
      return normalizeChallengeResponse(data);
    });
};

/**
 * Cerrar sesion
 */
const logout = () => {
  window.location.href = getLoginUrl();
};

const redirectToLogin = () => {
  window.location.href = getLoginUrl();
};

/**
 * Limpiar LocalStorage
 */
const clearStorage = () => {
  // localStorage.clear();
  localStorage.removeItem('ls.authorizationData');
  localStorage.removeItem('ls.credentials');
  localStorage.removeItem('creds');
  localStorage.removeItem('ls.infoUsuario');
  localStorage.removeItem('ID');
};

const recaptchaValidation = (token) => {
  const postData = {
    key: token
  };

  let enviroment = `${Environment.getCurrent().endpoints.admin}/Token/Recaptcha`;

  return axios.post(enviroment, postData)
    .then(({ data }) => {
      return data;
    });
};

export const AuthService = {
  login,
  interactiveLogin,
  verifyTwoFactorChallenge,
  resendTwoFactorChallenge,
  logout,
  redirectToLogin,
  getLoginUrl,
  clearStorage,
  isAuth,
  hasActiveSession,
  authentication,
  recaptchaValidation,
  persistAuthenticationData,
  isAuthenticatedResponse
};
